import { useCallback, useEffect, useMemo, useState } from 'react';
import { history, request } from 'cademy-shared-web';
import {
    ArrowLeftIcon,
    MenuIcon,
    Pane,
    Position,
    SideSheet,
    toaster,
    Dialog,
    Button,
    DocumentIcon,
    StarIcon,
} from 'evergreen-ui';
import { IconButton, ProfilePopover } from '../../components';
import ReactHtmlParser from 'react-html-parser';
import CourseSections from './components/CourseSections';
import Lesson from './components/Lesson';
import styles from './CourseViewer.module.css';
import { ButtonAnchor } from '../_ui/Buttons';
import { useReward } from 'react-rewards';
import { BookingDetails } from '../BookingDetails';
import { useBookingOrOrderDetailsLink } from '../../hooks/useBookingOrOrderDetailsLink';
import { useAppSelector } from 'store';
import { Environment } from 'services/Environment';
import selectIam from 'selectors/selectIam';

const getRefreshedData = async (recordType, recordUuid) => {
    const { data } = await request.get(`/get-course-lessons/${recordType}/${recordUuid}`);
    return data;
};

const getCompletedLessonsLength = (sections, lesson_progress, currentStudentId) => {
    if (!lesson_progress) return 0;
    const allLessons = sections.flatMap((section) => section.contents);
    const trueLessonCount = lesson_progress.filter((record) =>
        allLessons.some(
            (lesson) => record.lesson_ref === lesson.ref && lesson.student_ref === currentStudentId
        )
    );
    return trueLessonCount.filter((lp) => lp.completed_at > 0).length;
};

export const CourseViewer = ({ data = {}, setData }) => {
    const urlSearchParams = new URLSearchParams(history.location.search);
    const lessonParam = urlSearchParams.get('lesson') || '';
    const occurrenceParam = urlSearchParams.get('occurrence') || null;
    const [selectedLessonRef, setSelectedLessonRef] = useState(null);
    const [mobileMenuShown, setMobileMenuShown] = useState(false);
    const [updatingProgress, setUpdatingProgress] = useState(false);
    const [showCompletionDialog, setShowCompletionDialog] = useState(false);
    const iam = useAppSelector(selectIam);

    const currentStudentId = iam.authenticated ? iam.studentId : null;

    const singleLesson = useMemo(
        () =>
            data?.course?.sections?.length === 1 &&
            data?.course?.sections[0]?.contents?.length === 1,
        [data.course]
    );
    const { reward } = useReward('complete-lesson-button', 'emoji', {
        elementCount: 100,
        emoji: ['🎉', '🎉', '🎊', '🥳', '🎓'],
    });

    const recordType = useMemo(() => (data.booking ? 'booking' : 'order'), [data]);
    const record = useMemo(() => data[recordType], [data, recordType]);

    const refreshData = useCallback(async () => {
        const refreshedData = await getRefreshedData(recordType, record._id);
        setData(refreshedData);
    }, [recordType, record._id, setData]);

    const totalCurrentLessonsCount = useMemo(() => (data.lessons || []).length, [data.lessons]);
    const completedLessonLength = useMemo(() => {
        return getCompletedLessonsLength(
            data.course.sections,
            record.lesson_progress,
            currentStudentId
        );
    }, [currentStudentId, data.course.sections, record.lesson_progress]);
    const courseCompletePercentage = useMemo(
        () => Math.round((completedLessonLength / totalCurrentLessonsCount) * 100),
        [completedLessonLength, totalCurrentLessonsCount]
    );
    const lessonsLeft = useMemo(
        () => totalCurrentLessonsCount - completedLessonLength,
        [totalCurrentLessonsCount, completedLessonLength]
    );
    const lessonsRefs = useMemo(() => {
        return data.course.sections.reduce((arr, section) => {
            const sectionLessonRefs = section.contents
                .filter((c) => c.type === 'lesson')
                .map((c) => c.ref);
            return arr.concat(sectionLessonRefs);
        }, []);
    }, [data]);
    const completedLessonRefs = useMemo(
        () =>
            (record.lesson_progress || [])
                .filter((l) => l.completed_at && l.completed_at > 0)
                .map((lp) => lp.lesson_ref),
        [record]
    );
    const completed = useMemo(
        () => lessonsRefs.every((r) => completedLessonRefs.includes(r)),
        [lessonsRefs, completedLessonRefs]
    );
    const backButtonLink = useBookingOrOrderDetailsLink({
        educatorSlug: data?.educator?.slug,
        courseSlug: data?.course?.slug,
        recordType: recordType,
        recordId: record._id,
        occurrenceId: recordType === 'booking' ? record.occurrence_ref : null,
    });

    useEffect(() => {
        if (lessonParam) {
            // On initial load, select the lesson specified in the URL
            const lesson = data.lessons.find((lesson) => lesson._id === lessonParam);
            if (lesson) setSelectedLessonRef(lesson._id);
        } else if (!occurrenceParam && data?.lessons?.length) {
            // Default to the first lesson on course, or fallback to first first fetched lesson
            // The fallback is needed because of CAD-86
            const first_section = data?.course?.sections[0];
            const first_lesson_on_course = first_section?.contents[0];
            const found_lesson = data.lessons.find(
                (lesson) => lesson._id === first_lesson_on_course?.ref
            );
            const lesson = found_lesson || data.lessons[0];
            if (lesson) setSelectedLessonRef(lesson._id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateLessonProgress = useCallback(
        (lessonRef, status, override = false) => {
            const progress = (record.lesson_progress || []).find(
                (progress) =>
                    progress.lesson_ref === lessonRef && progress.student_ref === currentStudentId
            );

            if (!progress || !progress[`${status}_at`] || override) {
                setUpdatingProgress(true);
                request
                    .post(`/${recordType}s/${record._id}/lesson/${lessonRef}/${status}`)
                    .then(async ({ data: newRecord }) => {
                        await refreshData();

                        // If we were marking a lesson as complete, move to the next section
                        if (status === 'completed') {
                            const currentSectionIndex = data.course.sections.findIndex((section) =>
                                section.contents.find((lesson) => lesson.ref === lessonRef)
                            );
                            const currentLessonIndex = data.course.sections[
                                currentSectionIndex
                            ].contents.findIndex((lesson) => lesson.ref === lessonRef);

                            const isLastLesson =
                                completedLessonRefs.length + 1 === lessonsRefs.length;

                            if (isLastLesson) {
                                toaster.success(
                                    '🎉 Congratulations - You Have Completed The Course 🎉'
                                );
                                reward();
                                if (data.course.type === 'on-demand') {
                                    setShowCompletionDialog(true);
                                }
                                return;
                            }

                            if (
                                currentLessonIndex ===
                                data.course.sections[currentSectionIndex].contents.length - 1
                            ) {
                                // this is the last lesson in the current section
                                const nextSection = data.course.sections[currentSectionIndex + 1];
                                if (nextSection) {
                                    const nextLesson = nextSection.contents[0];
                                    setSelectedLessonRef(nextLesson.ref);
                                    toaster.success('👍 Well Done! Keep it up!');
                                } else {
                                    toaster.success('👍 Well Done!');
                                }
                            } else {
                                // this is not the last lesson in the current section
                                const nextLesson =
                                    data.course.sections[currentSectionIndex].contents[
                                        currentLessonIndex + 1
                                    ];
                                setSelectedLessonRef(nextLesson.ref);
                                toaster.success('👍 Well Done! Keep it up!!');
                            }
                        } else {
                            if (progress) toaster.notify('Lesson marked as incomplete.');
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                    })
                    .finally(() => {
                        setUpdatingProgress(false);
                    });
            }
        },
        [
            record.lesson_progress,
            record._id,
            currentStudentId,
            recordType,
            refreshData,
            data.course.sections,
            data.course.type,
            completedLessonRefs,
            lessonsRefs,
            reward,
        ]
    );

    useEffect(() => {
        if (selectedLessonRef) updateLessonProgress(selectedLessonRef, 'started');
    }, [selectedLessonRef, updateLessonProgress]);

    useEffect(() => {
        refreshData();
    }, [selectedLessonRef, refreshData]);

    useEffect(() => {
        setMobileMenuShown(false);
    }, [selectedLessonRef]);

    const renderContents = () => {
        const orderOrBooking = data.order || data.booking;
        if (
            orderOrBooking.payment_status === 'cancelled' ||
            orderOrBooking.payment_status === 'refunded'
        ) {
            return (
                <div style={{ textAlign: 'center', margin: '80px auto' }}>
                    <h2>Your order was cancelled</h2>
                    <h4>Please contact the educator directly if you wish to regain access</h4>
                </div>
            );
        }

        if (data.course.deleted_at) {
            return (
                <div style={{ textAlign: 'center', margin: '80px auto' }}>
                    <h2>This course was archived by the educator</h2>
                    <h4>Please contact the educator directly if you wish to regain access</h4>
                </div>
            );
        }

        if (data.course.hosted_externally) {
            return (
                <div style={{ textAlign: 'center', margin: '80px auto' }}>
                    <h2>This course is hosted externally</h2>
                    <h4>
                        {ReactHtmlParser(data.course.external_hosting_details) ||
                            'Check your email, there may be instructions for how to get access to this course outside of Cademy'}
                    </h4>
                </div>
            );
        }

        if (!data.lessons.length) {
            return (
                <div style={{ textAlign: 'center', padding: '80px' }}>
                    <h2>Looks like this course has no contents</h2>
                    <h4>
                        Check your email, there may be instructions for how to get access to this
                        course outside of Cademy
                    </h4>
                </div>
            );
        }

        if (data.lessons.length >= 1) {
            return (
                <>
                    {singleLesson ? null : (
                        <div className={styles.lessons_list_container}>
                            <CourseSections
                                course={data.course}
                                lessons={data.lessons}
                                occurrence={data.occurrence}
                                lessonProgress={record?.lesson_progress}
                                selectedLesson={data.lessons.find(
                                    (lesson) => lesson._id === selectedLessonRef
                                )}
                                setSelectedLesson={(lesson) => setSelectedLessonRef(lesson._id)}
                                courseCompletePercentage={courseCompletePercentage}
                                record={record}
                                completed={completed}
                                currentStudentId={currentStudentId}
                            />
                        </div>
                    )}
                    {occurrenceParam ? (
                        <div className={styles.lesson_container}>
                            <div className={styles.lesson_contents}>
                                <BookingDetails
                                    course={data.course}
                                    occurrence={data.occurrence}
                                    educator_slug={data.educator.slug}
                                />
                            </div>
                        </div>
                    ) : null}
                    {selectedLessonRef && !occurrenceParam ? (
                        <Lesson
                            lesson={data.lessons.find((lesson) => lesson._id === selectedLessonRef)}
                            media={data.media}
                            setSelectedLessonRef={(lessonRef) => {
                                const foundLesson = data.lessons.find(
                                    (lesson) => lesson._id === lessonRef
                                );
                                if (foundLesson) {
                                    setSelectedLessonRef(foundLesson._id);
                                    history.push(`?lesson=${foundLesson._id}`);
                                }
                            }}
                            scormLessonStatuses={data.scorm_lesson_statuses}
                            record={record}
                            recordType={recordType}
                            updateLessonProgress={updateLessonProgress}
                            lessonsLeft={lessonsLeft}
                            course={data.course}
                            singleLesson={singleLesson}
                            refreshData={refreshData}
                            updatingProgress={updatingProgress}
                            currentStudentId={currentStudentId}
                        />
                    ) : null}
                </>
            );
        }

        return null; //should not be possible
    };

    return (
        <Pane>
            <div className={styles.nav}>
                <div className={styles.back_container}>
                    {(data.lessons || []).length < 2 ? null : (
                        <div className={styles.menu_btn_wrapper}>
                            <IconButton onClick={() => setMobileMenuShown(true)}>
                                <MenuIcon />
                            </IconButton>
                        </div>
                    )}
                    <ButtonAnchor href={backButtonLink} target="_self">
                        <ArrowLeftIcon marginRight="10px" />
                        <span className={styles.back_label}>Back</span>
                    </ButtonAnchor>
                </div>
                <h3 id="course-name" className={styles.courseName}>
                    {data?.course?.name}
                </h3>
                <div className={styles.popoverContainer}>
                    <ProfilePopover />
                </div>
            </div>
            <div
                className={
                    singleLesson || !data.course || data.course?.deleted_at || !data.lessons?.length
                        ? styles.single_col_page
                        : styles.two_column_page
                }
            >
                {renderContents()}
            </div>
            <SideSheet
                width="85vw"
                position={Position.LEFT}
                isShown={mobileMenuShown}
                onCloseComplete={() => setMobileMenuShown(false)}
                containerProps={{ style: { maxHeight: '100dvh' } }}
            >
                <CourseSections
                    course={data.course}
                    lessons={data.lessons}
                    occurrence={data.occurrence}
                    lessonProgress={record?.lesson_progress}
                    selectedLesson={data.lessons.find((lesson) => lesson._id === selectedLessonRef)}
                    setSelectedLesson={(lesson) => setSelectedLessonRef(lesson._id)}
                    courseCompletePercentage={courseCompletePercentage}
                    record={record}
                    currentStudentId={currentStudentId}
                />
            </SideSheet>
            <Dialog
                isShown={showCompletionDialog}
                title="Congratulations 🎉"
                onCloseComplete={() => setShowCompletionDialog(false)}
                confirmLabel="Close"
                hasFooter={false}
            >
                <Pane paddingBottom={20}>
                    <Pane>
                        You have successfully completed this {data?.course?.keyword || 'course'}!
                        Please take a moment to add a review or download your certificate.
                    </Pane>
                    <Pane display="flex" gap={10} marginTop={20} justifyContent="stretch">
                        {data?.course?.certificate?.automatic_issuing?.enabled && (
                            <Button
                                appearance="primary"
                                iconBefore={DocumentIcon}
                                is="a"
                                href={`${Environment.API_URL}/certificates/completion/${record._id}${currentStudentId ? `?guest=${currentStudentId}` : ''}`}
                                target="_blank"
                                width="100%"
                            >
                                Get Certificate
                            </Button>
                        )}
                        <Button
                            appearance="default"
                            is="a"
                            href={
                                data.educator.slug && data.course.slug
                                    ? `https://${data.educator.slug}.${Environment.DOMAIN}/${data.course.slug}/add-review`
                                    : '#'
                            }
                            target="_blank"
                            iconBefore={StarIcon}
                            width="100%"
                        >
                            Add a Review
                        </Button>
                    </Pane>
                </Pane>
            </Dialog>
        </Pane>
    );
};
